export function isPersistedRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export type PersistMigrationStep<T> = {
  version: number;
  migrate: (state: T) => T;
};

export type PersistMigrationDefinition<T> = {
  version: number;
  steps: readonly PersistMigrationStep<T>[];
  fallback: () => T;
};

export function runPersistMigrations<T>({
  state,
  fromVersion,
  toVersion,
  steps,
}: {
  state: T;
  fromVersion: number;
  toVersion: number;
  steps: readonly PersistMigrationStep<T>[];
}): T {
  let current = state;

  for (const step of [...steps].sort(
    (left, right) => left.version - right.version,
  )) {
    if (fromVersion < step.version && step.version <= toVersion) {
      current = step.migrate(current);
    }
  }

  return current;
}

export function createPersistMigrator<T extends Record<string, unknown>>(
  definition: PersistMigrationDefinition<T>,
): (persistedState: unknown, version: number) => T {
  const { toVersion, steps, fallback } = {
    toVersion: definition.version,
    ...definition,
  };

  return (persistedState, version) => {
    if (!isPersistedRecord(persistedState)) {
      return fallback();
    }

    const state = persistedState as T;

    if (version >= toVersion) {
      return state;
    }

    return runPersistMigrations({
      state,
      fromVersion: version,
      toVersion,
      steps,
    });
  };
}
